import { MeasurementUnit } from './metrics';

// Describes a single dimension setting returned from the GET endpoint
export interface DimensionSetting {
  dimension: string; // e.g., 'weight', 'length'
  selectedUnitId: number;
  units: MeasurementUnit[];
}

// Describes the payload for the PUT endpoint
export interface UpdateDimensionSettingPayload {
  dimension: string;
  unitId: number;
}

// Sub-structures for DailyNormsResponse
export interface CalculationStep {
  name: string;
  step: number;
  value: number | string | object;
  formula: string;
}

export interface UsedData {
  goal: string;
  atomic?: {
    age?: number;
    gender?: string;
    weight?: number;
    height?: number;
    activityLevel?: string;
  };
  calculated?: {
    targetCalories?: number;
  };
  dietScheme?: string;
}

export interface CalculatorInfo {
  name: string;
  infoLink: string | null;
}

export interface CalculationDetails {
  steps: CalculationStep[];
  usedData: UsedData;
  calculators: CalculatorInfo[];
}

export interface GoalCalculationDetails {
  water: CalculationDetails;
  calories: CalculationDetails;
  nutrients: CalculationDetails;
}

// Main data structure inside the API response
/** manual - норми блоку введено вручну. */
export type DailyNormCalculationStatus = 'calculated' | 'missing_data' | 'error' | 'not_calculated' | 'manual';

export interface DailyNormBlockStatus {
  status: DailyNormCalculationStatus;
  /** Відсутні дані клієнта (weight, height, age, gender, activityLevel) - лише для missing_data. */
  missingFields: string[];
}

// Норма null - не розрахована (показувати прочерк); причина - calculationStatus.
export interface DailyNormsData {
  waterMl: number | null;
  caloriesIntake: number | null;
  caloriesBurned: number | null;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  calculationDetails?: GoalCalculationDetails | null;
  /** water - вода; calories - калорії, витрати на активність і БЖВ. */
  calculationStatus: { water: DailyNormBlockStatus; calories: DailyNormBlockStatus };
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Response structure for GET /settings/daily-norms
 */
export interface DailyNormsResponse {
  success: boolean;
  data: DailyNormsData;
  meta: object;
}

/**
 * Request structure for PUT /settings/daily-norms
 */
/** Лише змінені поля; null - очистити норму (не задано). */
export interface UpdateDailyNormsRequest {
  waterMl?: number | null;
  caloriesIntake?: number | null;
  caloriesBurned?: number | null;
  proteinG?: number | null;
  fatG?: number | null;
  carbsG?: number | null;
}

export type DailyNormKey = 'waterMl' | 'caloriesIntake' | 'caloriesBurned' | 'proteinG' | 'fatG' | 'carbsG';

export const DAILY_NORM_KEYS: DailyNormKey[] = ['waterMl', 'caloriesIntake', 'caloriesBurned', 'proteinG', 'fatG', 'carbsG'];

/**
 * Власні норми експерта для клієнта (бачить лише експерт); null - не задано, діє стандартна. Версії: діє з effectiveFrom
 * (день клієнта) і далі; чинна - найновіша, що вже почала діяти.
 */
export type ExpertDailyNormsData = Record<DailyNormKey, number | null> & {
  effectiveFrom: string | null;
  updatedAt: string | null;
};

/** Версія з проміжком дії; effectiveTo null - діє досі. */
export type ExpertDailyNormsVersion = ExpertDailyNormsData & { effectiveFrom: string; effectiveTo: string | null };

/** Версія норм одного експерта для клієнта - історія для продакта / адміна. */
export interface ExpertDailyNormsListItem extends ExpertDailyNormsVersion {
  expertId: number;
  expertName: string;
}
